import React, { useEffect } from "react";
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import { LocationTypeSelector } from "pages/OceanWeatherIntelligence/OperationRuleLocation/Components/LocationTypeSelector";
import { observer } from "mobx-react-lite";
import { useStores } from "stores/setup/use-store";
import { BackButton } from "components/Map/BackButton";
import { MapPin } from "lucide-react";
import { IStation } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { MapPointPreview } from "components/Map/MapPointPreview";

interface AlertBasicInfoProps {
  t: (key: string) => string;
  errors: any;
  touched: any;
  setFieldValue: (field: string, value: any) => void;
  handleLocationTypeSelect: (
    type: "point" | "area",
    setFieldValue: (field: string, value: any) => void
  ) => void;
  handleBack: (setFieldValue: (field: string, value: any) => void) => void;
  alertLocationType: "point" | "area" | null;
  handlePointSelectionMode?: () => void;
  selectedStationForAlert?: IStation | null;
  isDetectingLocationType?: boolean;
}

export const AlertBasicInfo: React.FC<AlertBasicInfoProps> = observer(
  ({
    t,
    errors,
    touched,
    setFieldValue,
    handleLocationTypeSelect,
    handleBack,
    alertLocationType,
    handlePointSelectionMode,
    selectedStationForAlert,
    isDetectingLocationType,
  }) => {
    const {
      dataStores: { regionStore },
    } = useStores();

    useEffect(() => {
      if (selectedStationForAlert) {
        const macroRegionOrigin = selectedStationForAlert.macro_region_origin;
        const macroRegion = regionStore.macroRegions?.find(
          (region) => region.origin === macroRegionOrigin
        );

        if (macroRegion) {
          const stationRegion = `${selectedStationForAlert.station_id}-${macroRegion.id}`;
          setFieldValue("stationRegion", stationRegion);
          setFieldValue("macroRegionId", macroRegion.id);
        }
      }
    }, [selectedStationForAlert, setFieldValue, regionStore.macroRegions]);

    return (
      <>
        {isDetectingLocationType && (
          <Box borderWidth="1px" borderRadius="md" p={4}>
            <Skeleton height="20px" width="120px" mb={4} />
            <Skeleton height="200px" borderRadius="md" />
          </Box>
        )}

        {!alertLocationType && !isDetectingLocationType && (
          <>
            <LocationTypeSelector
              onSelect={(type) => handleLocationTypeSelect(type, setFieldValue)}
              t={t}
            />
            {(errors.geofence || errors.stationRegion) &&
              (touched.geofence || touched.stationRegion) && (
                <Text color="red.500" fontSize="sm" mt={2}>
                  {t("pleaseSelectPointOrArea")}
                </Text>
              )}
          </>
        )}

        {alertLocationType === "point" && selectedStationForAlert && (
          <Box borderWidth="1px" borderRadius="md" p={4}>
            <Flex gap="12px" mb="16px" justifyContent="space-between">
              <Flex gap="12px">
                <MapPin color="#086AA9" size="20px" />
                <Text fontWeight={"400"} fontSize="md" color="#086AA9">
                  {t("pointDetails")}
                </Text>
              </Flex>
              <BackButton
                onClick={() => handleBack(setFieldValue)}
                label={t("back") as string}
              />
            </Flex>

            <FormControl
              isRequired
              isInvalid={!!errors.stationRegion && touched.stationRegion}
            >
              <MapPointPreview
                station={selectedStationForAlert}
                onEdit={handlePointSelectionMode}
                onClear={() => handleBack(setFieldValue)}
                t={t}
              />
              {errors.stationRegion && touched.stationRegion && (
                <FormErrorMessage mt={2}>
                  {errors.stationRegion}
                </FormErrorMessage>
              )}
            </FormControl>
          </Box>
        )}

        {alertLocationType === "point" && !selectedStationForAlert && (
          <Box borderWidth="1px" borderRadius="md" p={4}>
            <Skeleton height="20px" width="120px" mb={4} />
            <Skeleton height="200px" borderRadius="md" />
          </Box>
        )}
      </>
    );
  }
);
